import { parseMathContent } from '@/shared/features/rich-text';

export type AiBuilderAssistantMarkdownBlock =
  | {
      type: 'paragraph';
      text: string;
    }
  | {
      type: 'heading';
      text: string;
    }
  | {
      type: 'list';
      items: string[];
    }
  | {
      type: 'quote';
      lines: string[];
    }
  | {
      type: 'spacer';
    };

export type AiBuilderInlineMarkdownToken =
  | {
      type: 'text' | 'bold' | 'italic';
      text: string;
    }
  | {
      type: 'math';
      latex: string;
      display: boolean;
    };

type AiBuilderInlineMathToken = Extract<
  AiBuilderInlineMarkdownToken,
  { type: 'math' }
>;

const BULLET_LINE_PATTERN = /^\s*-\s+(.+)$/;
const HEADING_LINE_PATTERN = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/;
const QUOTE_LINE_PATTERN = /^\s{0,3}>\s?(.*)$/;
const SEPARATOR_LINE_PATTERN = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
const INLINE_MARKDOWN_PATTERN =
  /\*\*(.+?)\*\*|\*([^\s*](?:[^*\n]*?[^\s*])?)\*|_([^\s_](?:[^_\n]*?[^\s_])?)_/g;

export const parseAiBuilderAssistantMarkdownBlocks = (
  content: string,
): AiBuilderAssistantMarkdownBlock[] => {
  const blocks: AiBuilderAssistantMarkdownBlock[] = [];

  content.split(/\r?\n/).forEach(line => {
    if (!line.trim() || SEPARATOR_LINE_PATTERN.test(line)) {
      pushSpacerBlock(blocks);
      return;
    }

    const quoteMatch = line.match(QUOTE_LINE_PATTERN);
    if (quoteMatch) {
      const previousBlock = blocks.at(-1);
      if (previousBlock?.type === 'quote') {
        previousBlock.lines.push(quoteMatch[1]);
        return;
      }

      blocks.push({
        type: 'quote',
        lines: [quoteMatch[1]],
      });
      return;
    }

    const headingMatch = line.match(HEADING_LINE_PATTERN);
    if (headingMatch) {
      blocks.push({
        type: 'heading',
        text: headingMatch[1],
      });
      return;
    }

    const bulletMatch = line.match(BULLET_LINE_PATTERN);

    if (!bulletMatch) {
      blocks.push({
        type: 'paragraph',
        text: line,
      });
      return;
    }

    const previousBlock = blocks.at(-1);
    if (previousBlock?.type === 'list') {
      previousBlock.items.push(bulletMatch[1]);
      return;
    }

    blocks.push({
      type: 'list',
      items: [bulletMatch[1]],
    });
  });

  return blocks;
};

const pushSpacerBlock = (blocks: AiBuilderAssistantMarkdownBlock[]) => {
  if (blocks.at(-1)?.type === 'spacer') return;
  blocks.push({ type: 'spacer' });
};

// PUA(Private Use Area) 문자라 실제 채팅 텍스트에는 등장하지 않는다.
const MATH_MASK_CHAR = '\uE000';

export const tokenizeAiBuilderInlineMarkdown = (
  text: string,
): AiBuilderInlineMarkdownToken[] => {
  // LaTeX의 `_`(첨자)·`*`(곱셈)를 italic 마크다운으로 오인해 수식이 깨지지
  // 않도록 수식을 먼저 마스킹하고, 마크다운 페어링(`**…$x$…**` 같은 수식을
  // 가로지르는 강조 포함)이 끝난 뒤 마스크 위치에 수식 토큰을 복원한다.
  const mathTokens: AiBuilderInlineMathToken[] = [];
  const maskedText = parseMathContent(text.split(MATH_MASK_CHAR).join(''))
    .map(segment => {
      if (segment.type !== 'math') {
        return segment.content;
      }

      mathTokens.push({
        type: 'math',
        latex: segment.content,
        display: segment.display,
      });
      return MATH_MASK_CHAR;
    })
    .join('');

  const tokens: AiBuilderInlineMarkdownToken[] = [];
  let mathTokenIndex = 0;

  const pushTokensWithMathRestored = (
    type: 'text' | 'bold' | 'italic',
    tokenText: string,
  ) => {
    tokenText.split(MATH_MASK_CHAR).forEach((part, partIndex) => {
      if (partIndex > 0) {
        tokens.push(mathTokens[mathTokenIndex]);
        mathTokenIndex += 1;
      }
      if (part) {
        tokens.push({ type, text: part });
      }
    });
  };

  let lastIndex = 0;

  for (const match of maskedText.matchAll(INLINE_MARKDOWN_PATTERN)) {
    const matchText = match[0];
    const boldText = match[1];
    const italicText = match[2] ?? match[3];
    const matchStartIndex = match.index ?? 0;
    const matchEndIndex = matchStartIndex + matchText.length;

    if (matchStartIndex > lastIndex) {
      pushTokensWithMathRestored(
        'text',
        maskedText.slice(lastIndex, matchStartIndex),
      );
    }

    if (boldText !== undefined) {
      pushTokensWithMathRestored('bold', boldText);
    } else {
      pushTokensWithMathRestored('italic', italicText);
    }

    lastIndex = matchEndIndex;
  }

  if (lastIndex < maskedText.length) {
    pushTokensWithMathRestored('text', maskedText.slice(lastIndex));
  }

  return tokens;
};
